import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'apollo-react/components/Button';
import Typography from '../../../StyledTypography';
import styles from './SignatureCanvas.module.css';

/**
 * SignatureCanvas - A canvas-based signature pad component
 *
 * @param {string} value - Base64 encoded signature image
 * @param {Function} onChange - Callback when signature changes
 * @param {boolean} disabled - Whether the signature pad is disabled
 */
const SignatureCanvas = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Track if canvas has been initialized
  const [canvasInitialized, setCanvasInitialized] = useState(false);

  // Initialize canvas on mount
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Set canvas drawing surface to match displayed size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Fill with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    setCanvasInitialized(true);
  }, []);

  /**
   * Load signature when value changes (e.g., from draft load).
   * Runs after canvas is initialized to ensure drawing surface is ready.
   * This is critical for draft loading where value arrives asynchronously.
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvasInitialized) return;

    // Only load if we have a valid signature value
    if (value && typeof value === 'string' && value.startsWith('data:image')) {
      const ctx = canvas.getContext('2d');

      // Clear canvas first
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Load signature image
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.onerror = () => {
        console.warn('Failed to load signature image');
      };
      img.src = value;
    } else if (!value || value === '') {
      // Clear if value is empty (but not on initial mount without value)
      if (hasSignature) {
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
      }
    }
  }, [value, canvasInitialized, hasSignature]);

  // Get canvas coordinates from event (accounting for scaling)
  const getCoordinates = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // Calculate scale factors between canvas size and displayed size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }, []);

  // Start drawing
  const startDrawing = useCallback(
    (e) => {
      if (disabled) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const { x, y } = getCoordinates(e);

      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
    },
    [disabled, getCoordinates]
  );

  // Draw
  const draw = useCallback(
    (e) => {
      if (!isDrawing || disabled) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const { x, y } = getCoordinates(e);

      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#000000';
      ctx.lineTo(x, y);
      ctx.stroke();
      setHasSignature(true);
    },
    [isDrawing, disabled, getCoordinates]
  );

  // Stop drawing and save
  const stopDrawing = useCallback(() => {
    if (!isDrawing) return;

    setIsDrawing(false);

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Save signature as base64
    const dataUrl = canvas.toDataURL('image/png');
    // console.log(
    //   '[SignatureCanvas] Signature captured:',
    //   `length=${dataUrl.length}, prefix=${dataUrl.substring(0, 50)}...`
    // );
    onChange(dataUrl);
  }, [isDrawing, onChange]);

  // Clear signature
  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onChange('');
  }, [onChange]);

  return (
    <div className={styles.signatureContainer}>
      <canvas
        ref={canvasRef}
        className={`${styles.canvas} ${disabled ? styles.disabled : ''}`}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      {!hasSignature && !disabled && (
        <Typography
          variant="bodySmall"
          color="textSecondary"
          className={styles.placeholder}
        >
          {t('formRenderer.signatureSignHere')}
        </Typography>
      )}
      {!disabled && hasSignature && (
        <Button
          variant="secondary"
          size="small"
          onClick={clearSignature}
          className={styles.clearButton}
        >
          {t('formRenderer.signatureClear')}
        </Button>
      )}
    </div>
  );
};

export default SignatureCanvas;
